<script setup lang="ts">
import ToolCornerMenuButton from "../toolbar/ToolCornerMenuButton.vue";
import {
  POLAR_ANGLE_OPTIONS,
  useSnapControls,
} from "@/composable/useSnapControls";
import type { PolarAngleMode } from "@/stores/DraggerMenuState";

const { isPolarActive, polarAngleMode, togglePolar, setPolarAngleMode } =
  useSnapControls();

function pickAngle(mode: PolarAngleMode, close: () => void) {
  close();
  setPolarAngleMode(mode);
}
</script>

<template>
  <ToolCornerMenuButton
    phosphor-icon="angle"
    :mode-on="isPolarActive"
    :tooltip-title="$t('canvas:tools.snap.polarTitle')"
    :tooltip-text="$t('canvas:tools.snap.polar')"
    :menu-label="$t('canvas:tools.snap.polarMenuTitle')"
    @toggle="togglePolar"
  >
    <template #badge>
      <span v-if="isPolarActive" class="angle-badge"
        >{{ polarAngleMode }}°</span
      >
    </template>
    <template #default="{ close }">
      <div class="snap-popover-content" role="menu">
        <div class="snap-popover-label">
          {{ $t("canvas:tools.snap.polarAngleTitle") }}
        </div>
        <!-- L'incrément retenu reste marqué même polaire coupé : c'est
             celui que rallume le bouton. -->
        <button
          v-for="option in POLAR_ANGLE_OPTIONS"
          :key="option.mode"
          type="button"
          role="menuitemradio"
          class="snap-option"
          :class="{ active: polarAngleMode === option.mode }"
          :aria-checked="polarAngleMode === option.mode"
          @click="pickAngle(option.mode, close)"
        >
          <span class="snap-angle">{{ option.mode }}°</span>
          <span class="snap-hint">{{ option.hint }}</span>
        </button>
      </div>
    </template>
  </ToolCornerMenuButton>
</template>

<style scoped>
/* À cheval sur le coin haut droit, comme une pastille : lisible, et le coin
   bas droit garde la flèche des options. */
.angle-badge {
  position: absolute;
  top: -5px;
  /* Pas au-delà du bord de la barre (4px de marge de chaque côté). */
  right: -4px;
  z-index: 2;
  padding: 1px 4px;
  border-radius: 7px;
  background-color: var(--canvas-btn-active-bg);
  box-shadow: 0 0 0 2px var(--canvas-bg);
  color: #1a1a1a;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  pointer-events: none;
}
</style>
